import { useCallback, useEffect, FC, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment, useRefetchableFragment } from 'react-relay';
import { useIntl } from 'dibs-react-intl';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
import { getFilterValue } from '../../finding/SbSharedRefineMenu/sbSharedRefineMenuHelpers';

import { SharedFollowSearchProviderRefetchQuery } from './__generated__/SharedFollowSearchProviderRefetchQuery.graphql';
import { SharedFollowSearchProvider_itemSearch$key } from './__generated__/SharedFollowSearchProvider_itemSearch.graphql';
import { SharedFollowSearchProvider_user$key } from './__generated__/SharedFollowSearchProvider_user.graphql';

type RelayVariables = {
    fetchUser: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

export type SharedFollowSearchProviderChildrenProps = {
    showFollowButton: boolean;
    isFollowing: boolean;
    followSearchValue: string;
    refetchFollow: (callback?: () => void) => void;
    isCategorySearch: boolean;
    followPageServiceId?: string | null;
    thinTrackingEnabled?: boolean;
};

type Props = {
    children: (props: SharedFollowSearchProviderChildrenProps) => ReactElement;
    itemSearch: SharedFollowSearchProvider_itemSearch$key;
    user: SharedFollowSearchProvider_user$key;
    searchTerm?: string;
    thinTrackingEnabled?: boolean;
    uriRef?: string | null;
};

export const SharedFollowSearchProvider: FC<Props> = ({
    children,
    itemSearch: itemSearchRef,
    searchTerm,
    user: userRef,
    thinTrackingEnabled,
    uriRef,
}) => {
    const intl = useIntl();

    const [user, refetch] = useRefetchableFragment<
        SharedFollowSearchProviderRefetchQuery,
        SharedFollowSearchProvider_user$key
    >(
        graphql`
            fragment SharedFollowSearchProvider_user on User
            @refetchable(queryName: "SharedFollowSearchProviderRefetchQuery") {
                # needed since this refetch fragment is on User, and not Viewer
                id
                favorites(first: 1, types: $followSearchTypes, pages: $followSearchPages) {
                    ...SaveSearchButton_favoritesConnection
                    edges {
                        node {
                            serviceId
                        }
                    }
                }
            }
        `,
        userRef
    );

    const itemSearch = useFragment(
        graphql`
            fragment SharedFollowSearchProvider_itemSearch on ItemSearchQueryConnection {
                pageType
                canFollowSearch
                displayUriRef
                meta {
                    header
                }
                appliedFilters {
                    name
                    values {
                        displayName
                        urlLabel
                        code
                    }
                }
            }
        `,
        itemSearchRef
    );

    const { appliedFilters, canFollowSearch, pageType } = itemSearch;
    let followSearchValue = searchTerm || itemSearch?.meta?.header || '';

    const { fetchUser } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const serviceId = user?.favorites?.edges?.[0]?.node?.serviceId;
    const refetchId = user?.id;

    useEffect(() => {
        if (fetchUser && uriRef && refetchId) {
            refetch({ followSearchPages: [uriRef], id: refetchId });
        }
    }, [fetchUser, uriRef, refetch, refetchId]);

    if (pageType === pageTypes.MORE_FROM_SELLER) {
        const sellerName = getFilterValue(appliedFilters || [], 'sellerPk')?.displayName;
        followSearchValue = intl.formatMessage(
            {
                id: 'abf.custom.moreFromSeller.name.value',
                defaultMessage: 'All Items from {sellerName}',
            },
            { sellerName }
        );
    }

    const refetchFollow = useCallback(
        (onComplete: () => void = () => {}): void => {
            if (uriRef && fetchUser) {
                refetch(
                    { followSearchPages: [uriRef] },
                    { fetchPolicy: 'network-only', onComplete }
                );
            }
        },
        [fetchUser, uriRef, refetch]
    );

    return children({
        /**  show for:
         * - page type can follow search (from graphql)
         * - if the user isn't already following this search
         */
        showFollowButton: !!canFollowSearch,
        isFollowing: !!serviceId,
        followSearchValue,
        refetchFollow,
        isCategorySearch: !searchTerm,
        followPageServiceId: serviceId,
        thinTrackingEnabled,
    });
};
